import React, { useState, useEffect } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  IconButton,
  useMediaQuery,
  useTheme
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import logoImage from '../../assets/logo_image.png';
import { useAuth } from '../../contexts/AuthContext';
import './Header.css';

const Header = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();

  const { isAuthenticated, isApproved, isPending, logout } = useAuth();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/');
    setMobileMenuOpen(false);
  };

  const navItems = [
    { name: 'Experiences', path: '/experiences' },
  ];

  const approvedNavItems = [
    { name: 'My Bookings', path: '/my-bookings' },
  ];

  const closeMobileMenu = () => setMobileMenuOpen(false);

  return (
    <header className={`premium-header ${scrolled ? 'scrolled' : ''}`}>
      <div className="header-container">
        {/* Logo */}
        <RouterLink to="/" className="header-logo">
          <img src={logoImage} alt="Bosphorus Yachts" className="logo-image" />
          <span className="logo-text">
            Bosphorus <span className="logo-accent">Affiliates</span>
          </span>
        </RouterLink>

        {/* Desktop Navigation */}
        <nav className="desktop-nav">
          {navItems.map((item) => (
            <RouterLink key={item.name} to={item.path} className="nav-link">
              {item.name}
            </RouterLink>
          ))}
          
          {!isAuthenticated && (
            <>
              <RouterLink to="/login" className="nav-link">
                Login
              </RouterLink>
              <RouterLink to="/signup" className="nav-btn primary">
                Sign Up
              </RouterLink>
            </>
          )}
          
          {isAuthenticated && (
            <RouterLink to="/profile" className="nav-link">
              Profile
            </RouterLink>
          )}
          
          {isApproved && approvedNavItems.map((item) => (
            <RouterLink key={item.name} to={item.path} className="nav-link">
              {item.name}
            </RouterLink>
          ))}

          {isApproved && (
            <RouterLink to="/dashboard" className="nav-btn secondary">
              Dashboard
            </RouterLink>
          )}
          
          {isAuthenticated && (
            <button onClick={handleLogout} className="nav-link logout-btn">
              Sign Out
            </button>
          )}
        </nav>

        {/* Mobile Menu Button */}
        <button 
          className="mobile-menu-btn"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label="Toggle menu"
        >
          {mobileMenuOpen ? <CloseIcon /> : <MenuIcon />}
        </button>
      </div>

      {/* Mobile Navigation */}
      <div className={`mobile-nav ${mobileMenuOpen ? 'open' : ''}`}>
        <div className="mobile-nav-content">
          {navItems.map((item) => (
            <RouterLink 
              key={item.name} 
              to={item.path} 
              className="mobile-nav-link"
              onClick={closeMobileMenu}
            >
              {item.name}
            </RouterLink>
          ))}
          
          {!isAuthenticated && (
            <>
              <RouterLink to="/login" className="mobile-nav-link" onClick={closeMobileMenu}>
                Login
              </RouterLink>
              <RouterLink to="/signup" className="mobile-nav-btn primary" onClick={closeMobileMenu}>
                Sign Up
              </RouterLink>
            </>
          )}
          
          {isAuthenticated && (
            <RouterLink to="/profile" className="mobile-nav-link" onClick={closeMobileMenu}>
              Profile
            </RouterLink>
          )}
          
          {isApproved && approvedNavItems.map((item) => (
            <RouterLink key={item.name} to={item.path} className="mobile-nav-link" onClick={closeMobileMenu}>
              {item.name}
            </RouterLink>
          ))}

          {isApproved && (
            <RouterLink to="/dashboard" className="mobile-nav-btn secondary" onClick={closeMobileMenu}>
              Dashboard
            </RouterLink>
          )}
          
          {isAuthenticated && (
            <button onClick={handleLogout} className="mobile-nav-link logout-btn">
              Sign Out
            </button>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;
